/**
 * 模組頁面代碼 : 1340-UI-05
 * 頁面說明 : 職位資訊列表畫面
 * @module web/modules/access-management/components/PositionListModal
 */


// ----- import from npm modules ----
import $ from 'jquery';
import React, { useEffect } from 'react';

// ----- import from local modules -----
import {
    AccessModeEnums,
    ModuleEnums,
} from '../../../components/BaseComponent';
import {
    DataTableComponent,
} from '../../../components/CommonComponent';
import { AlertModalModeEnums } from 'web/components/AlertModal';

const PositionListModal = React.forwardRef((props, ref) => {
    const { checkPermission, onNewPosition, onEditPosition, onEditPositionStatus, positionList, needPositionListLoad, setNeedPositionListLoad } = props;

    const preparePositionListTable = () => {
        // 定義資料內容
        const dataSet = positionList.map((position) => {
            return [
                position.positionId,
                position.positionName,
                position.positionDescription,
                position.flgStatus,
            ];
        });

        // 定義欄位屬性
        const columnConfig = [
            { title: '職位名稱', data: 1, width: '30%' },
            { title: '職位說明', data: 2, width: '50%' },
            {
                orderable: false,
                order: null,
                title: '啟用',
                data: 3,
                className: 'dt-center',
                render: (data, type, row, meta) => {
                    let enableId = 'eab-posi-' + meta.row;
                    let isChecked = data === 'A' ? 'checked' : '';
                    let enableButton = checkPermission(ModuleEnums.POSITION, AccessModeEnums.DELETE) ? '' : 'disabled';
                    return `<div class="form-check form-switch">
                               <input class="form-check-input" type="checkbox" role="switch" id="${enableId}" ${isChecked} ${enableButton} />
                               <label class="form-check-label" for="${enableId}"></label>
                            </div>`;
                },
            },
            {
                orderable: false,
                order: null,
                data: null,
                className: 'dt-right',
                render: (data, type, row, meta) => {
                    let editId = 'edit-posi-' + meta.row;
                    let editButton = checkPermission(ModuleEnums.POSITION, AccessModeEnums.UPDATE) ?
                        `<button type="button" class="btn btn-sm btn-link" id="${editId}">
                            <svg class="btn__icon btn__icon--sm me-1" aria-hidden="true">
                                <use href="#ico-edit"></use>
                            </svg>
                            修改
                        </button>` : '';
                    return `<div class="btn-group" role="group" aria-label="">
                                ${editButton}
                            </div>`;
                },
                width: '20%',
            },
        ];

        // 定義排序設定
        const orderConfig = {
            idx: 0,
            dir: 'asc',
        };

        // 定義固定欄位設定
        const fixedColumnsConfig = {
            start: 1,
        };

        // 先清除datatable
        if ($.fn.DataTable.isDataTable('#datatable__positionList')) {
            $('#datatable__positionList').empty();
            $('#datatable__positionList').DataTable().destroy();
        }
        
        return DataTableComponent('#datatable__positionList', dataSet, columnConfig, orderConfig, fixedColumnsConfig);
    };

    // 當表格的容器可見後，調整列寬
    const adjustTableWhenVisible = () => {
        const tableContainer = document.querySelector('#datatable__positionList'); // 假設這是表格的容器
        if (tableContainer && tableContainer.offsetParent !== null) {
            let t = preparePositionListTable();
            t.columns.adjust().draw();
        } else {
            setTimeout(adjustTableWhenVisible, 100); // 容器不可見，稍後再嘗試
        }
    };

    useEffect(() => {
        if (needPositionListLoad) {
            setNeedPositionListLoad(false);

            const maskElement = document.querySelector('#modal-mask');
            // 顯示遮罩
            maskElement.style.opacity = '0.5';

            // 重新繪製表格
            adjustTableWhenVisible();

            // 隱藏遮罩
            maskElement.style.opacity = '0'
            setTimeout(() => {
               maskElement.style.display = 'none'
            }, 200);

            if (ref.current) {
                ref.current.addEventListener('click', function (event) {
                    const button = event.target.closest('.btn');
                    if (button && button.id.startsWith('edit-posi-')) {
                        const index = parseInt(button.id.split('-')[2]);
                        onEditPosition(index);
                    }
                });
                ref.current.addEventListener('change', function (event) {
                    const checkbox = event.target.closest('input[type="checkbox"]');
                    if (checkbox && checkbox.id.startsWith('eab-posi-')) {
                        const mode = checkbox.checked ? AlertModalModeEnums.ENABLE : AlertModalModeEnums.DISABLE;
                        const index = parseInt(checkbox.id.split('-')[2]);
                        const updatePosition = positionList[index];
                        let message = '';
                        if (mode === AlertModalModeEnums.ENABLE) {
                            message = `啟用 [ ${updatePosition.positionName} ]`;
                        } else {
                            message = `停用 [ ${updatePosition.positionName} ]`;
                        }
                        onEditPositionStatus(mode, message, updatePosition.positionId);
                    }
                });
            }
        }
    }, [needPositionListLoad]);

    return (
        <div ref={ref}
            className="modal fade"
            id="modalPositionList"
            data-bs-backdrop="static"
            data-bs-keyboard="false"
            tabIndex="-1"
            aria-labelledby="modalPositionListLabel"
            aria-hidden="true"
        >
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                <form className="modal__form modal-content needs-validation" id="modalPositionListForm">
                    <div className="modal-header">
                        <h1 className="modal-title fs-5" id="modalPositionListLabel">
                            <div className="modal__title__badge">
                                <svg className="badge__icon" aria-hidden="true">
                                    <use href="#ico-users-edit"></use>
                                </svg>
                            </div>
                            <div className="modal__title__text">職位資訊</div>
                        </h1>
                        <button type="button" className="btn-close btn-cancel" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        <div id="modal-mask" className="modal-mask"></div>

                        <div className="datatable__tool mb-2">
                            <div className="d-flex align-items-center gap-2 ms-auto">
                                {checkPermission(ModuleEnums.POSITION, AccessModeEnums.CREATE) && (
                                <button type="button" className="btn btn-sm btn-primary" id="modalPositionBtnNew" onClick={onNewPosition}>
                                    <svg className="btn__icon me-2" aria-hidden="true">
                                        <use href="#ico-plus"></use>
                                    </svg>
                                    新增
                                </button>
                                )}
                            </div>
                        </div>
                        <div className="datatable__data">
                            <table id="datatable__positionList" className="display table table-hover align-middle nowrap" style={{ width: '100%' }}></table>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
});

export default PositionListModal;